import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider, NewIdentifier } from "../providers/types";
import { type PickedRecord, RecordPicker } from "./RecordPicker";
import { type ControlledDialogProps, useDialogState } from "./useDialogState";

/** The identifiers the form asks for, in order. */
const IDENTIFIER_FIELDS = [
  { id_type: "email", label: "Email" },
  { id_type: "phone", label: "Phone" },
  { id_type: "nmls", label: "NMLS" },
];

const ERRORS: Record<string, string> = {
  name_required: "Give the contact a first or last name",
  invalid_identifier: "One of the identifiers is not in a valid format",
  unknown_company: "That company could not be found",
};

/** What a match shares with the new contact, as the warning says it. */
const REASON_LABEL: Record<string, string> = {
  name: "name",
  email: "email",
  phone: "phone",
  nmls: "NMLS",
};

/**
 * `value` once it has stopped changing for `ms` (the duplicate check). A
 * string, so an equal value from a new render does not restart the wait.
 */
function useSettled(value: string, ms = 400): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(handle);
  }, [value, ms]);
  return settled;
}

/**
 * New contact (W10 H2): name, types, email / phone / NMLS and an optional
 * company. Lists existing contacts with the same name or identifier before
 * saving (warned, not blocked: decision 3), then opens the new contact.
 */
export function ContactCreateDialog(props: ControlledDialogProps = {}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const notify = useNotify();
  const { isOpen, setIsOpen, showTrigger } = useDialogState(props);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [company, setCompany] = useState<PickedRecord | null>(null);
  const [role, setRole] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const catalogs = useQuery({
    queryKey: ["catalogs"],
    queryFn: () => dataProvider.getCatalogs(),
    enabled: isOpen,
  });
  const identifiers: NewIdentifier[] = IDENTIFIER_FIELDS.map((f) => ({
    id_type: f.id_type,
    value: values[f.id_type] ?? "",
  })).filter((i) => i.value.trim());
  const lookup = JSON.parse(
    useSettled(JSON.stringify({ firstName, lastName, identifiers })),
  ) as { firstName: string; lastName: string; identifiers: NewIdentifier[] };
  const matches = useQuery({
    queryKey: ["contact_matches", lookup],
    enabled:
      isOpen &&
      (Boolean(lookup.firstName.trim() && lookup.lastName.trim()) ||
        lookup.identifiers.length > 0),
    queryFn: () =>
      dataProvider.findContactMatches({
        first_name: lookup.firstName,
        last_name: lookup.lastName,
        identifiers: lookup.identifiers,
      }),
  });

  const close = () => {
    setIsOpen(false);
    setFirstName("");
    setLastName("");
    setTypes([]);
    setValues({});
    setCompany(null);
    setRole("");
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const { id } = await dataProvider.createContact({
        first_name: firstName,
        last_name: lastName,
        types,
        identifiers,
        company_id: company?.id ?? null,
        role: company ? role : null,
      });
      close();
      navigate(`/contacts/${id}/show`);
    } catch (error) {
      const body = (error as { body?: { error?: string; id_type?: string } })
        .body;
      notify(
        body?.error === "identifier_taken"
          ? `That ${REASON_LABEL[body.id_type ?? ""] ?? "identifier"} already belongs to another contact`
          : (ERRORS[body?.error ?? ""] ?? "The contact could not be saved"),
        { type: "error" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {showTrigger ? (
        <Button size="sm" onClick={() => setIsOpen(true)}>
          New contact
        </Button>
      ) : null}
      <Dialog open={isOpen} onOpenChange={(open) => (open ? null : close())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New contact</DialogTitle>
            <DialogDescription>
              One person, one contact: check the matches below before saving.
            </DialogDescription>
          </DialogHeader>
          <form
            aria-label="New contact"
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="new-first-name">First name</Label>
                <Input
                  id="new-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-last-name">Last name</Label>
                <Input
                  id="new-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              {IDENTIFIER_FIELDS.map((field) => (
                <div key={field.id_type} className="space-y-1">
                  <Label htmlFor={`new-${field.id_type}`}>{field.label}</Label>
                  <Input
                    id={`new-${field.id_type}`}
                    value={values[field.id_type] ?? ""}
                    onChange={(e) =>
                      setValues({ ...values, [field.id_type]: e.target.value })
                    }
                  />
                </div>
              ))}
            </div>

            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">Types</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {(catalogs.data?.contact_types ?? []).map((type) => (
                  <label
                    key={type.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={types.includes(type.id)}
                      onCheckedChange={(checked) =>
                        setTypes(
                          checked
                            ? [...types, type.id]
                            : types.filter((t) => t !== type.id),
                        )
                      }
                    />
                    {type.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                The first one ticked is the primary type.
              </p>
            </fieldset>

            <RecordPicker
              id="new-company"
              label="Company (optional)"
              objectType="company"
              picked={company}
              onPick={setCompany}
              pickedText="Member of"
            />
            {company ? (
              <div className="space-y-1">
                <Label htmlFor="new-role">Role at {company.label}</Label>
                <Input
                  id="new-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                />
              </div>
            ) : null}

            {(matches.data ?? []).length ? (
              <div
                role="status"
                className="rounded-md border border-amber-500/50 p-3 text-sm"
              >
                <p className="font-medium">Possible duplicates</p>
                <ul className="mt-1 space-y-1">
                  {(matches.data ?? []).map((match) => (
                    <li key={match.id}>
                      <Link
                        className="underline"
                        to={`/contacts/${match.id}/show`}
                        onClick={close}
                      >
                        {match.name}
                      </Link>{" "}
                      <span className="text-muted-foreground">
                        (same {REASON_LABEL[match.reason] ?? match.reason})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || !(firstName.trim() || lastName.trim())}
              >
                Save contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
